import type { QueryClient } from '@tanstack/react-query';
import type { SelectableRoleAssignmentsResponse } from '@pillar-portal/api-types';
import { runInBackground } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';
import { useBreadcrumbStore } from '@/store/breadcrumbs';
import { invalidateSessionSensitiveRequests } from '@/lib/sessionSensitiveRequestManager';

export interface EffectiveSessionRefreshLogger {
    error: (message: string, err: unknown) => void;
}

export function setSelectedRoleAssignmentInCache(
    queryClient: QueryClient,
    roleAssignmentUuid: string
): void {
    queryClient.setQueryData<SelectableRoleAssignmentsResponse>(
        ['selectableRoleAssignments'],
        (selectableRoleAssignmentsResponse) =>
            selectableRoleAssignmentsResponse === undefined
                ? undefined
                : {
                      ...selectableRoleAssignmentsResponse,
                      selected_role_assignment_uuid: roleAssignmentUuid,
                  }
    );
}

export function resetReactQueryAfterRoleSwitch(
    queryClient: QueryClient,
    log: EffectiveSessionRefreshLogger
): void {
    useAuthStore.getState().beginEffectiveSessionRefresh();
    useBreadcrumbStore.getState().reset();
    invalidateSessionSensitiveRequests();

    runInBackground(
        (async () => {
            await queryClient.cancelQueries();
            await queryClient.resetQueries({ predicate: () => true });
        })(),
        (err) => {
            log.error('Error resetting queries after effective session change', err);
        }
    );
}

export function clearReactQueryAfterPartialRoleSwitch(
    queryClient: QueryClient,
    log: EffectiveSessionRefreshLogger
): void {
    const cancelQueriesPromise: Promise<void> = queryClient.cancelQueries();
    queryClient.removeQueries({
        predicate: ({ queryKey }) =>
            queryKey.length !== 1 || queryKey[0] !== 'selectableRoleAssignments',
    });
    useAuthStore.getState().beginEffectiveSessionRefresh();
    invalidateSessionSensitiveRequests();

    runInBackground(cancelQueriesPromise, (err) => {
        log.error('Error clearing queries after partial role switch', err);
    });
}
